import { __decorate, __extends } from "tslib";
import { serialize, serializeAsVector3 } from "../Misc/decorators.js";
import { Matrix, Vector3 } from "../Maths/math.vector.js";
import { Light } from "./light.js";
import { Axis } from "../Maths/math.axis.js";
/**
 * Base implementation IShadowLight
 * It groups all the common behaviour in order to reduce duplication and better follow the DRY pattern.
 */
var ShadowLight = /** @class */ (function (_super) {
    __extends(ShadowLight, _super);
    function ShadowLight() {
        var _this = _super !== null && _super.apply(this, arguments) || this;
        _this._needProjectionMatrixCompute = true;
        return _this;
    }
    ShadowLight.prototype._setPosition = function (value) {
        this._position = value;
    };
    Object.defineProperty(ShadowLight.prototype, "position", {
        /**
         * Sets the position the shadow will be casted from. Also use as the light position for both
         * point and spot lights.
         */
        get: function () {
            return this._position;
        },
        /**
         * Sets the position the shadow will be casted from. Also use as the light position for both
         * point and spot lights.
         */
        set: function (value) {
            this._setPosition(value);
        },
        enumerable: false,
        configurable: true
    });
    ShadowLight.prototype._setDirection = function (value) {
        this._direction = value;
    };
    Object.defineProperty(ShadowLight.prototype, "direction", {
        /**
         * In 2d mode (needCube being false), gets the direction used to cast the shadow.
         * Also use as the light direction on spot and directional lights.
         */
        get: function () {
            return this._direction;
        },
        /**
         * In 2d mode (needCube being false), sets the direction used to cast the shadow.
         * Also use as the light direction on spot and directional lights.
         */
        set: function (value) {
            this._setDirection(value);
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ShadowLight.prototype, "shadowMinZ", {
        /**
         * Gets the shadow projection clipping minimum z value.
         */
        get: function () {
            return this._shadowMinZ;
        },
        /**
         * Sets the shadow projection clipping minimum z value.
         */
        set: function (value) {
            this._shadowMinZ = value;
            this.forceProjectionMatrixCompute();
        },
        enumerable: false,
        configurable: true
    });
    Object.defineProperty(ShadowLight.prototype, "shadowMaxZ", {
        /**
         * Sets the shadow projection clipping maximum z value.
         */
        get: function () {
            return this._shadowMaxZ;
        },
        /**
         * Gets the shadow projection clipping maximum z value.
         */
        set: function (value) {
            this._shadowMaxZ = value;
            this.forceProjectionMatrixCompute();
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Computes the transformed information (transformedPosition and transformedDirection in World space) of the current light
     * @returns true if the information has been computed, false if it does not need to (no parenting)
     */
    ShadowLight.prototype.computeTransformedInformation = function () {
        if (this.parent && this.parent.getWorldMatrix) {
            if (!this.transformedPosition) {
                this.transformedPosition = Vector3.Zero();
            }
            Vector3.TransformCoordinatesToRef(this.position, this.parent.getWorldMatrix(), this.transformedPosition);
            // In case the direction is present.
            if (this.direction) {
                if (!this.transformedDirection) {
                    this.transformedDirection = Vector3.Zero();
                }
                Vector3.TransformNormalToRef(this.direction, this.parent.getWorldMatrix(), this.transformedDirection);
            }
            return true;
        }
        return false;
    };
    /**
     * Return the depth scale used for the shadow map.
     * @returns the depth scale.
     */
    ShadowLight.prototype.getDepthScale = function () {
        return 50.0;
    };
    /**
     * Get the direction to use to render the shadow map. In case of cube texture, the face index can be passed.
     * @param faceIndex The index of the face we are computed the direction to generate shadow
     * @returns The set direction in 2d mode otherwise the direction to the cubemap face if needCube() is true
     */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    ShadowLight.prototype.getShadowDirection = function (faceIndex) {
        return this.transformedDirection ? this.transformedDirection : this.direction;
    };
    /**
     * Returns the ShadowLight absolute position in the World.
     * @returns the position vector in world space
     */
    ShadowLight.prototype.getAbsolutePosition = function () {
        return this.transformedPosition ? this.transformedPosition : this.position;
    };
    /**
     * Sets the ShadowLight direction toward the passed target.
     * @param target The point to target in local space
     * @returns the updated ShadowLight direction
     */
    ShadowLight.prototype.setDirectionToTarget = function (target) {
        this.direction = Vector3.Normalize(target.subtract(this.position));
        return this.direction;
    };
    /**
     * Returns the light rotation in euler definition.
     * @returns the x y z rotation in local space.
     */
    ShadowLight.prototype.getRotation = function () {
        this.direction.normalize();
        var xaxis = Vector3.Cross(this.direction, Axis.Y);
        var yaxis = Vector3.Cross(xaxis, this.direction);
        return Vector3.RotationFromAxis(xaxis, yaxis, this.direction);
    };
    /**
     * Returns whether or not the shadow generation require a cube texture or a 2d texture.
     * @returns true if a cube texture needs to be use
     */
    ShadowLight.prototype.needCube = function () {
        return false;
    };
    /**
     * Detects if the projection matrix requires to be recomputed this frame.
     * @returns true if it requires to be recomputed otherwise, false.
     */
    ShadowLight.prototype.needProjectionMatrixCompute = function () {
        return this._needProjectionMatrixCompute;
    };
    /**
     * Forces the shadow generator to recompute the projection matrix even if position and direction did not changed.
     */
    ShadowLight.prototype.forceProjectionMatrixCompute = function () {
        this._needProjectionMatrixCompute = true;
    };
    /** @hidden */
    ShadowLight.prototype._initCache = function () {
        _super.prototype._initCache.call(this);
        this._cache.position = Vector3.Zero();
    };
    /** @hidden */
    ShadowLight.prototype._isSynchronized = function () {
        if (!this._cache.position.equals(this.position)) {
            return false;
        }
        return true;
    };
    /**
     * Computes the world matrix of the node
     * @param force defines if the cache version should be invalidated forcing the world matrix to be created from scratch
     * @returns the world matrix
     */
    ShadowLight.prototype.computeWorldMatrix = function (force) {
        if (!force && this.isSynchronized()) {
            this._currentRenderId = this.getScene().getRenderId();
            return this._worldMatrix;
        }
        this._updateCache();
        this._cache.position.copyFrom(this.position);
        if (!this._worldMatrix) {
            this._worldMatrix = Matrix.Identity();
        }
        Matrix.TranslationToRef(this.position.x, this.position.y, this.position.z, this._worldMatrix);
        if (this.parent && this.parent.getWorldMatrix) {
            this._worldMatrix.multiplyToRef(this.parent.getWorldMatrix(), this._worldMatrix);
            this._markSyncedWithParent();
        }
        // Cache the determinant
        this._worldMatrixDeterminantIsDirty = true;
        return this._worldMatrix;
    };
    /**
     * Gets the minZ used for shadow according to both the scene and the light.
     * @param activeCamera The camera we are returning the min for
     * @returns the depth min z
     */
    ShadowLight.prototype.getDepthMinZ = function (activeCamera) {
        return this.shadowMinZ !== undefined ? this.shadowMinZ : activeCamera.minZ;
    };
    /**
     * Gets the maxZ used for shadow according to both the scene and the light.
     * @param activeCamera The camera we are returning the max for
     * @returns the depth max z
     */
    ShadowLight.prototype.getDepthMaxZ = function (activeCamera) {
        return this.shadowMaxZ !== undefined ? this.shadowMaxZ : activeCamera.maxZ;
    };
    /**
     * Sets the shadow projection matrix in parameter to the generated projection matrix.
     * @param matrix The matrix to updated with the projection information
     * @param viewMatrix The transform matrix of the light
     * @param renderList The list of mesh to render in the map
     * @returns The current light
     */
    ShadowLight.prototype.setShadowProjectionMatrix = function (matrix, viewMatrix, renderList) {
        if (this.customProjectionMatrixBuilder) {
            this.customProjectionMatrixBuilder(viewMatrix, renderList, matrix);
        }
        else {
            this._setDefaultShadowProjectionMatrix(matrix, viewMatrix, renderList);
        }
        return this;
    };
    /** @hidden */
    ShadowLight.prototype._syncParentEnabledState = function () {
        _super.prototype._syncParentEnabledState.call(this);
        if (!this.parent || !this.parent.getWorldMatrix) {
            this.transformedPosition = null;
            this.transformedDirection = null;
        }
    };
    __decorate([
        serializeAsVector3()
    ], ShadowLight.prototype, "position", null);
    __decorate([
        serializeAsVector3()
    ], ShadowLight.prototype, "direction", null);
    __decorate([
        serialize()
    ], ShadowLight.prototype, "shadowMinZ", null);
    __decorate([
        serialize()
    ], ShadowLight.prototype, "shadowMaxZ", null);
    return ShadowLight;
}(Light));
export { ShadowLight };
//# sourceMappingURL=shadowLight.js.map